import Avatar from '@/components/ui/avatar'
import CompanyMark from '@/components/generals/company-mark'
import { OWNERS } from '@/constants/companies'
import { useCrmStore } from '@/stores/crm-store-provider'

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/ui/select'

const NONE = '__none__'

interface SelectFieldProps {
    id: string
    value: string
    onChange: (value: string) => void
    disabled?: boolean
}

interface OptionSelectProps extends SelectFieldProps {
    options: ReadonlyArray<string>
}

export function OptionSelect({ id, value, onChange, options, disabled }: OptionSelectProps) {
    return (
        <Select value={value} onValueChange={onChange} disabled={disabled}>
            <SelectTrigger id={id}>
                <SelectValue />
            </SelectTrigger>
            <SelectContent>
                {options.map(option => (
                    <SelectItem key={option} value={option}>
                        {option}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    )
}

export function OwnerSelect({ id, value, onChange, disabled }: SelectFieldProps) {
    return (
        <Select value={value} onValueChange={onChange} disabled={disabled}>
            <SelectTrigger id={id}>
                <SelectValue />
            </SelectTrigger>
            <SelectContent>
                {OWNERS.map(owner => (
                    <SelectItem key={owner.name} value={owner.name}>
                        <span className='flex items-center gap-2'>
                            <Avatar src={owner.avatar} alt='' />
                            {owner.name}
                        </span>
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    )
}

interface CompanySelectProps extends SelectFieldProps {
    placeholder?: string
}

export function CompanySelect({ id, value, onChange, disabled, placeholder = "Choose a company" }: CompanySelectProps) {
    const companies = useCrmStore(state => state.companies)
    const sorted = [...companies].sort((a, b) => a.name.localeCompare(b.name))

    return (
        <Select value={value} onValueChange={onChange} disabled={disabled}>
            <SelectTrigger id={id}>
                <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent>
                {sorted.map(company => (
                    <SelectItem key={company.id} value={company.id}>
                        <span className='flex items-center gap-2'>
                            <CompanyMark company={company} className='size-5 rounded-[5px]' />
                            {company.name}
                        </span>
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    )
}

interface ContactSelectProps extends SelectFieldProps {
    companyId: string
}

export function ContactSelect({ id, value, onChange, disabled, companyId }: ContactSelectProps) {
    const contacts = useCrmStore(state => state.contacts)
    const options = contacts.filter(contact => !companyId || contact.companyId === companyId)

    return (
        <Select
            value={value || NONE}
            onValueChange={next => onChange(next === NONE ? '' : next)}
            disabled={disabled}
        >
            <SelectTrigger id={id}>
                <SelectValue />
            </SelectTrigger>
            <SelectContent>
                <SelectItem value={NONE}>
                    No contact
                </SelectItem>
                {options.map(contact => (
                    <SelectItem key={contact.id} value={contact.id}>
                        {contact.name}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    )
}
